import { useEffect, useMemo, useState } from "react";
import { api, ApiError, type AdminSetting } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Eye, EyeOff, Save, Trash2, Database, FileKey, Wand2 } from "lucide-react";

// Admin UI for DB-backed app settings (mail, S3, API-Football, VAPID, AI).
// Boot-critical secrets (DATABASE_URL, JWT_SECRET, COOKIE_SECRET, SECRETS_KEY,
// PUBLIC_BASE_URL, REDIS_URL) are NOT here — they live in .env because the
// app needs them before it can read the DB.

export default function SettingsPanel() {
  const [items, setItems] = useState<AdminSetting[]>([]);
  const [loading, setLoading] = useState(true);
  // Per-key edit state. Keys not in the map = field is in "view-only" mode.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const list = await api.admin.settings.list();
      setItems(list);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const groups = useMemo(() => {
    const out = new Map<string, AdminSetting[]>();
    items.forEach((it) => {
      if (!out.has(it.group)) out.set(it.group, []);
      out.get(it.group)!.push(it);
    });
    return Array.from(out.entries());
  }, [items]);

  const reveal = async (key: string) => {
    try {
      const r = await api.admin.settings.reveal(key);
      setDrafts((d) => ({ ...d, [key]: r.value ?? "" }));
      setRevealed((s) => {
        const n = new Set(s);
        n.add(key);
        return n;
      });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler");
    }
  };

  const startEdit = (key: string) => {
    setDrafts((d) => ({ ...d, [key]: d[key] ?? "" }));
  };

  const cancel = (key: string) => {
    setDrafts((d) => {
      const n = { ...d };
      delete n[key];
      return n;
    });
    setRevealed((s) => {
      const n = new Set(s);
      n.delete(key);
      return n;
    });
  };

  const save = async (key: string) => {
    const value = drafts[key];
    if (value == null) return;
    setSaving(key);
    try {
      if (value.length === 0) {
        await api.admin.settings.clear(key);
        toast.success(`${key} gelöscht`);
      } else {
        await api.admin.settings.put(key, value);
        toast.success(`${key} gespeichert`);
      }
      cancel(key);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setSaving(null);
    }
  };

  const clearSetting = async (key: string) => {
    if (!confirm(`Wert für ${key} wirklich aus der DB löschen? (.env-Wert bleibt aktiv, falls vorhanden)`)) return;
    setSaving(key);
    try {
      await api.admin.settings.clear(key);
      toast.success(`${key} gelöscht`);
      cancel(key);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler");
    } finally {
      setSaving(null);
    }
  };

  return (
    <section>
      <h2 className="font-display font-bold text-lg mb-2 flex items-center gap-2">
        <FileKey className="h-5 w-5" /> Einstellungen
      </h2>
      <div className="bg-card rounded-lg shadow-card p-4 space-y-4">
        <p className="text-xs text-muted-foreground">
          Anwendungs-Konfiguration. Werte werden in der Datenbank gespeichert (Secrets verschlüsselt mit AES-256-GCM)
          und überschreiben gleichnamige <code className="font-mono text-[10px] bg-muted px-1 rounded">.env</code>-Variablen.
          Boot-Secrets (<code className="text-[10px]">DATABASE_URL</code>, <code className="text-[10px]">JWT_SECRET</code>,
          <code className="text-[10px]">COOKIE_SECRET</code>, <code className="text-[10px]">SECRETS_KEY</code>,
          <code className="text-[10px]">PUBLIC_BASE_URL</code>, <code className="text-[10px]">REDIS_URL</code>) bleiben
          in der <code className="text-[10px]">.env</code>.
        </p>

        {loading ? (
          <div className="text-center py-6 text-sm text-muted-foreground">Lade …</div>
        ) : (
          // 2-column grid on wide screens (admin page is max-w-6xl, plenty of
          // room for two stacked groups). Falls back to single column < md.
          <div className="grid md:grid-cols-2 gap-3">
          {groups.map(([groupName, groupItems]) => (
            <details key={groupName} className="rounded-md border border-border self-start" open={groupItems.some((i) => !i.isSet)}>
              <summary className="cursor-pointer px-3 py-2 text-sm font-bold flex items-center justify-between gap-2">
                <span className="flex-1">{groupName}</span>
                <span className="text-[10px] font-normal text-muted-foreground">
                  {groupItems.filter((i) => i.isSet).length} / {groupItems.length} gesetzt
                </span>
                {groupName === "Web Push" && <GenerateVapidButton onDone={load} />}
              </summary>
              <div className="divide-y divide-border">
                {groupItems.map((s) => {
                  const isEditing = drafts[s.key] != null;
                  const isRevealed = revealed.has(s.key);
                  return (
                    <div key={s.key} className="px-3 py-3 space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold flex items-center gap-1.5">
                            {s.label}
                            {s.type === "secret" && <FileKey className="h-3 w-3 text-muted-foreground" />}
                          </div>
                          <div className="text-[10px] text-muted-foreground font-mono">{s.key}</div>
                        </div>
                        <SourceBadge source={s.source} />
                      </div>
                      {s.hint && <p className="text-[10px] text-muted-foreground">{s.hint}</p>}

                      {!isEditing ? (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => (s.type === "secret" ? reveal(s.key) : startEdit(s.key))}
                            className="h-8 text-xs"
                          >
                            {s.isSet ? "Bearbeiten" : "Setzen"}
                          </Button>
                          {s.source === "db" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => clearSetting(s.key)}
                              disabled={saving === s.key}
                              className="h-8 text-xs text-destructive"
                            >
                              <Trash2 className="h-3 w-3 mr-1" /> DB-Wert löschen
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Input
                            type={s.type === "secret" && !isRevealed ? "password" : "text"}
                            value={drafts[s.key]}
                            onChange={(e) => setDrafts((d) => ({ ...d, [s.key]: e.target.value }))}
                            className="h-9 text-sm font-mono"
                            placeholder={s.type === "secret" ? "(neuer Wert)" : ""}
                          />
                          {s.type === "secret" && (
                            <button
                              type="button"
                              onClick={() =>
                                setRevealed((set) => {
                                  const n = new Set(set);
                                  n.has(s.key) ? n.delete(s.key) : n.add(s.key);
                                  return n;
                                })
                              }
                              className="px-2 text-muted-foreground tap"
                              aria-label={isRevealed ? "Wert verbergen" : "Wert anzeigen"}
                            >
                              {isRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          )}
                          <Button
                            size="sm"
                            onClick={() => save(s.key)}
                            disabled={saving === s.key}
                            className="h-9 bg-accent text-accent-foreground hover:bg-accent/90 font-bold"
                          >
                            <Save className="h-3.5 w-3.5 mr-1" /> Speichern
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => cancel(s.key)}
                            disabled={saving === s.key}
                            className="h-9"
                          >
                            Abbrechen
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </details>
          ))}
          </div>
        )}
      </div>
    </section>
  );
}

// Generates a fresh VAPID keypair on the server (uses web-push's own keygen),
// saves both keys + a default subject, refreshes the panel. Stops the click
// from toggling the <details> via stopPropagation — the button lives inside
// the <summary>.
function GenerateVapidButton({ onDone }: { onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      onClick={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (
          !confirm(
            "Neuen VAPID-Schlüssel generieren?\n\n" +
              "Achtung: Bestehende Push-Abos werden ungültig — alle Nutzer:innen müssen Push neu erlauben.",
          )
        )
          return;
        setBusy(true);
        try {
          const r = await api.admin.settings.generateVapid();
          toast.success(`VAPID-Schlüssel generiert (Subject: ${r.subject})`);
          onDone();
        } catch (err) {
          toast.error(err instanceof ApiError ? err.message : "Fehler");
        } finally {
          setBusy(false);
        }
      }}
      disabled={busy}
      className="inline-flex items-center gap-1 text-[10px] font-bold bg-accent/15 text-accent hover:bg-accent/25 rounded-pill px-2 py-1 tap disabled:opacity-50"
      title="Erzeugt einen neuen VAPID-Keypair und speichert ihn"
    >
      <Wand2 className="h-3 w-3" />
      {busy ? "…" : "Schlüssel generieren"}
    </button>
  );
}

function SourceBadge({ source }: { source: "db" | "env" | "unset" }) {
  if (source === "db") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-success/15 text-success rounded-pill px-2 py-0.5">
        <Database className="h-3 w-3" /> DB
      </span>
    );
  }
  if (source === "env") {
    return <span className="text-[10px] font-bold bg-muted rounded-pill px-2 py-0.5">via .env</span>;
  }
  return <span className="text-[10px] font-bold bg-destructive/15 text-destructive rounded-pill px-2 py-0.5">— nicht gesetzt</span>;
}
